"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import {
  FaLaptopCode,
  FaMobileAlt,
  FaPaintBrush,
  FaCogs,
  FaBrain,
} from "react-icons/fa";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";
import { validateAndSanitizeCareersForm, isRateLimited } from "../../utils/security";

const lightGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};
const darkGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};
const greenOrangeGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.10) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};

const careersSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "EmployerAggregateRating",
  "itemReviewed": {
    "@type": "Organization",
    "name": "Fiieve",
    "url": "https://fiieve.com",
    "logo": "https://fiieve.com/Images/logo.png",
    "description": "Fiieve is a UK-based software and engineering company hiring frontend, backend, mobile, UI/UX, and AI automation specialists.",
    "sameAs": ["https://www.linkedin.com/company/fiieve", "https://twitter.com/fiieve"]
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",    "item": "https://fiieve.com/" },
      { "@type": "ListItem", "position": 2, "name": "Careers", "item": "https://fiieve.com/careers" }
    ]
  }
});

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};
const staggerContainer = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.15 } },
};

// ✅ EmailJS credentials from .env
const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CAREERS_TEMPLATE;
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function CareerPage() {
  const { isDark, theme } = useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const formRef = useRef(null);

  const [formData, setFormData]         = useState({ name: "", email: "", phone: "", position: "", experience: "", message: "" });
  const [errors, setErrors]             = useState({});
  const [submitted, setSubmitted]       = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError]   = useState("");

  const isGreenOrange = theme === "greenOrange";
  const isLight       = !isDark && !isGreenOrange;

  const sectionBg   = isDark ? "bg-black text-white" : isGreenOrange ? "bg-[#0d1a0e] text-white" : "bg-[#f0f4f0] text-gray-900";
  const sectionGrid = isLight ? lightGridStyle : isGreenOrange ? greenOrangeGridStyle : darkGridStyle;
  const headingText = isDark || isGreenOrange ? "text-white"    : "text-gray-900";
  const subText     = isDark || isGreenOrange ? "text-gray-400" : "text-gray-600";
  const accentText  = isGreenOrange ? "text-green-400" : isDark ? "text-green-400" : "text-green-700";
  const cardBg      = isDark ? "bg-gray-900" : "bg-[#1a2e1a]";
  const formBg      = isDark ? "bg-gray-900" : "bg-[#1a2e1a]";
  const inputBg     = isDark ? "bg-black"    : "bg-[#0d1a0e]";

  const openRoles = [
    { title: "Frontend Developer",        department: "Engineering",  type: "Remote", icon: <FaLaptopCode /> },
    { title: "Backend Developer",         department: "Engineering",  type: "Remote", icon: <FaLaptopCode /> },
    { title: "Full Stack Developer",      department: "Engineering",  type: "Remote", icon: <FaLaptopCode /> },
    { title: "UI/UX Designer",            department: "Design",       type: "Remote", icon: <FaPaintBrush /> },
    { title: "Graphic Designer",          department: "Design",       type: "Remote", icon: <FaPaintBrush /> },
    { title: "Mobile App Developer",      department: "Engineering",  type: "Remote", icon: <FaMobileAlt /> },
    { title: "Custom Software Developer", department: "Engineering",  type: "Remote", icon: <FaCogs /> },
    { title: "AI Automation Specialist",  department: "AI & Data",    type: "Remote", icon: <FaBrain /> },
    { title: "IoT Engineer",              department: "Engineering",  type: "Remote", icon: <FaCogs /> },
    { title: "Embedded Systems Engineer", department: "Engineering",  type: "Remote", icon: <FaCogs /> },
    { title: "Robotics Engineer",         department: "Engineering",  type: "Remote", icon: <FaCogs /> },
    { title: "PCB Design Engineer",       department: "Engineering",  type: "Remote", icon: <FaCogs /> },
    { title: "Smart Home Specialist",     department: "Engineering",  type: "Remote", icon: <FaCogs /> },
  ];

  const benefits = [
    { title: "Real-World Projects",  desc: "Work on live web apps, mobile platforms, AI systems, and IoT solutions for startups and enterprises across the UK." },
    { title: "Remote-First Culture", desc: "Fully remote and hybrid options available — we trust our team to deliver great work from wherever they are." },
    { title: "Mentorship & Growth",  desc: "Learn from senior engineers, get structured feedback, and level up your skills across the full tech stack." },
    { title: "Impactful Work",       desc: "Every project you touch helps a real business grow — your code and designs create measurable results." },
  ];

  const validateForm = () => {
    const newErrors = {};
    ["name", "email", "phone"].forEach((field) => {
      if (!formData[field]?.trim()) newErrors[field] = "This field is required";
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Please enter a valid email address";
    if (formData.phone && !/^[\d\s\-+]+$/.test(formData.phone))
      newErrors.phone = "Please enter a valid phone number";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (submitError) setSubmitError("");
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (isRateLimited("careers", 30000)) {
    setSubmitError("Please wait 30 seconds before submitting again.");
    return;
  }

  const { errors, isValid, sanitized } = validateAndSanitizeCareersForm(formData);
  if (!isValid) {
    setErrors(errors);
    return;
  }

  setIsSubmitting(true);
  setSubmitError("");

  try {
    await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
      from_name:  sanitized.name,
      from_email: sanitized.email,
      reply_to:   sanitized.email,
      phone:      sanitized.phone,
      position:   sanitized.position   || "General Application",
      experience: sanitized.experience || "Not specified",
      message:    sanitized.message    || "No cover letter provided",
    }, PUBLIC_KEY);

    setSubmitted(true);
    setFormData({ name: "", email: "", phone: "", position: "", experience: "", message: "" });
    setTimeout(() => setSubmitted(false), 6000);
  } catch (error) {
    setSubmitError("Unable to send your application. Please try again or email us at info@fiieve.com");
  } finally {
    setIsSubmitting(false);
  }
};

  // ✅ Apply button pre-fills the position dropdown and scrolls to form
  const scrollToForm = (roleTitle) => {
    setFormData((prev) => ({ ...prev, position: roleTitle }));
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const typeBadge = (type) =>
    type === "Remote"
      ? "bg-green-400/10 text-green-400 border border-green-400/30"
      : "bg-orange-400/10 text-orange-400 border border-orange-400/30";

  return (
    <>
      <SEOHead
        title="Careers at Fiieve | Join Our Software & Engineering Team"
        description="Join Fiieve — a UK software and engineering company. We're hiring frontend, backend, mobile, UI/UX, and AI automation specialists. Apply now and grow your career."
        canonical="https://fiieve.com/careers"
        ogImage="https://fiieve.com/Images/og-careers.jpg"
        schema={careersSchema}
      />

      <motion.main
        className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${sectionBg}`}
        style={sectionGrid}
        initial="hidden" animate="show" variants={fadeUp}
      >
        {isLight && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position:"absolute", top:"5%", left:"-8%", width:"45vw", height:"45vw", background:"radial-gradient(circle, rgba(74,222,128,0.11) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"5%", right:"-8%", width:"40vw", height:"40vw", background:"radial-gradient(circle, rgba(251,146,60,0.09) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"50%", left:"20%", width:"60vw", height:"40vw", background:"radial-gradient(ellipse, rgba(74,222,128,0.07) 0%, transparent 65%)", filter:"blur(55px)" }} />
          </div>
        )}
        {isGreenOrange && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position:"absolute", top:"5%", left:"-8%", width:"45vw", height:"45vw", background:"radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"5%", right:"-8%", width:"40vw", height:"40vw", background:"radial-gradient(circle, rgba(251,146,60,0.14) 0%, transparent 70%)", filter:"blur(45px)" }} />
          </div>
        )}

        <div className="relative" style={{ zIndex: 1 }}>

          {/* ── HERO BANNER ── */}
          <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Images/bg.png')" }} aria-hidden="true" />
            <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
              <h1 className="text-3xl md:text-5xl font-extrabold text-green-400 tracking-wide">Careers</h1>
              <nav aria-label="Breadcrumb" className="mt-3">
                <ol className="flex items-center justify-center gap-1 text-sm text-white">
                  <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li aria-current="page" className="text-green-400">Careers</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* ── CULTURE PITCH ── */}
          <section aria-labelledby="culture-heading" className="px-6 md:px-12 lg:px-20 pt-10 pb-6 text-center max-w-6xl mx-auto w-full">
            <motion.p className={`text-sm font-semibold uppercase tracking-widest ${accentText} mb-1`} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>Join Our Team</motion.p>
            <motion.h2 id="culture-heading" className={`text-2xl sm:text-3xl md:text-4xl font-bold ${headingText} mb-4`} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
              Build the Future of <span className={accentText}>Software & Engineering</span>
            </motion.h2>
            <motion.p className={`${isDark ? "text-gray-400" : "text-gray-700"} text-base md:text-lg leading-relaxed`} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
              At Fiieve, we're not just building software — we're building a team of people who care deeply about quality, innovation, and real-world impact. We work on challenging projects across{" "}
              <strong className={isDark ? "text-white" : "text-gray-900"}>web development</strong>,{" "}
              <strong className={isDark ? "text-white" : "text-gray-900"}>mobile apps</strong>,{" "}
              <strong className={isDark ? "text-white" : "text-gray-900"}>AI automation</strong>,{" "}
              <strong className={isDark ? "text-white" : "text-gray-900"}>IoT</strong>, and{" "}
              <strong className={isDark ? "text-white" : "text-gray-900"}>embedded systems</strong>{" "}
              for startups and enterprises across the UK and beyond.
            </motion.p>
          </section>

          {/* ── BENEFITS ── */}
          <section aria-labelledby="why-fiieve-heading" className="px-6 md:px-12 lg:px-20 py-6">
            <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <motion.p className={`text-sm font-semibold uppercase tracking-widest text-center ${accentText} mb-1`} variants={fadeUp}>Perks & Benefits</motion.p>
              <motion.h2 id="why-fiieve-heading" className={`text-2xl sm:text-3xl md:text-4xl font-bold ${headingText} text-center mb-6`} variants={fadeUp}>Why Work at Fiieve</motion.h2>
              <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto" variants={staggerContainer}>
                {benefits.map((item, index) => (
                  <motion.article key={index} variants={fadeUp} whileHover={{ scale: 1.05, boxShadow: "0 0 25px rgba(74,222,128,0.25)" }} aria-label={item.title}
                    className={`${cardBg} border border-green-400/30 hover:border-green-400/70 p-6 rounded-2xl text-center transition-all duration-300 flex flex-col`}>
                    <h3 className="text-green-400 font-semibold text-lg mb-3">{item.title}</h3>
                    <p className="text-gray-300 text-sm leading-relaxed flex-1">{item.desc}</p>
                  </motion.article>
                ))}
              </motion.div>
            </motion.div>
          </section>

          {/* ── OPEN ROLES ── */}
          <section aria-labelledby="open-roles-heading" className="px-6 md:px-12 lg:px-20 py-6">
            <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <motion.p className={`text-sm font-semibold uppercase tracking-widest text-center ${accentText} mb-1`} variants={fadeUp}>We're Hiring</motion.p>
              <motion.h2 id="open-roles-heading" className={`text-2xl sm:text-3xl md:text-4xl font-bold ${headingText} text-center mb-6`} variants={fadeUp}>Current Open Positions</motion.h2>
              <motion.div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4" variants={staggerContainer}>
                {openRoles.map((role) => (
                  <motion.div key={role.title} variants={fadeUp} whileHover={{ scale: 1.02, boxShadow: "0 0 20px rgba(74,222,128,0.2)" }}
                    className={`${cardBg} border border-green-400/30 hover:border-green-400/70 p-4 rounded-2xl transition-all duration-300`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="text-green-400 text-xl mt-0.5 flex-shrink-0" aria-hidden="true">{role.icon}</span>
                        <div>
                          <h3 className="text-white font-semibold text-base leading-tight">{role.title}</h3>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-gray-400 text-xs">{role.department}</span>
                            <span className="text-gray-600 text-xs" aria-hidden="true">·</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${typeBadge(role.type)}`}>{role.type}</span>
                          </div>
                        </div>
                      </div>
                      {/* ✅ Pre-fills position in form and scrolls down */}
                      <motion.button whileHover={{ scale: 1.1 }} onClick={() => scrollToForm(role.title)}
                        aria-label={`Apply for ${role.title} at Fiieve`}
                        className="bg-green-400 hover:bg-green-300 text-black px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 flex-shrink-0">
                        Apply
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </section>

          {/* ── APPLICATION FORM ── */}
          <section ref={formRef} aria-labelledby="apply-form-heading" className="px-6 md:px-12 lg:px-20 py-8">
            <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <motion.p className={`text-sm font-semibold uppercase tracking-widest text-center ${accentText} mb-1`} variants={fadeUp}>Get In Touch</motion.p>
              <motion.h2 id="apply-form-heading" className={`text-2xl sm:text-3xl md:text-4xl font-bold ${headingText} text-center mb-2`} variants={fadeUp}>Apply Now</motion.h2>
              <motion.p className={`${subText} text-center max-w-xl mx-auto mb-6 text-sm`} variants={fadeUp}>
                Don't see a role that matches your skills? Submit a general application — we're always looking for talented people to join the Fiieve team.
              </motion.p>

              <motion.form onSubmit={handleSubmit} className={`max-w-5xl mx-auto space-y-4 ${formBg} border border-green-400/30 rounded-2xl p-6`} variants={staggerContainer} noValidate>

                {/* Row 1: Name + Email */}
                <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4" variants={staggerContainer}>
                  {[
                    { field: "name",  label: "Full Name",     placeholder: "Enter your full name",     type: "text"  },
                    { field: "email", label: "Email Address", placeholder: "Enter your email address", type: "email" },
                  ].map(({ field, label, placeholder, type }) => (
                    <motion.div key={field} variants={fadeUp}>
                      <label htmlFor={field} className="block text-white text-sm font-medium mb-1">{label} <span className="text-red-400">*</span></label>
                      <input id={field} type={type} name={field} value={formData[field]} onChange={handleInputChange}
                        placeholder={placeholder} disabled={isSubmitting} aria-required="true"
                        aria-describedby={errors[field] ? `${field}-error` : undefined}
                        className={`w-full px-4 py-2.5 ${inputBg} border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-400 transition-colors text-sm ${errors[field] ? "border-red-500" : "border-gray-700"}`}
                      />
                      {errors[field] && <p id={`${field}-error`} className="text-red-400 text-xs mt-1">{errors[field]}</p>}
                    </motion.div>
                  ))}
                </motion.div>

                {/* Row 2: Phone + Experience */}
                <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4" variants={staggerContainer}>
                  <motion.div variants={fadeUp}>
                    <label htmlFor="phone" className="block text-white text-sm font-medium mb-1">Phone Number <span className="text-red-400">*</span></label>
                    <input id="phone" type="text" name="phone" value={formData.phone} onChange={handleInputChange}
                      placeholder="Enter your phone number" disabled={isSubmitting} aria-required="true"
                      aria-describedby={errors.phone ? "phone-error" : undefined}
                      className={`w-full px-4 py-2.5 ${inputBg} border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-400 transition-colors text-sm ${errors.phone ? "border-red-500" : "border-gray-700"}`}
                    />
                    {errors.phone && <p id="phone-error" className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                  </motion.div>
                  <motion.div variants={fadeUp}>
                    <label htmlFor="experience" className="block text-white text-sm font-medium mb-1">Years of Experience <span className="text-gray-500 font-normal">(optional)</span></label>
                    <select id="experience" name="experience" value={formData.experience} onChange={handleInputChange} disabled={isSubmitting}
                      className={`w-full px-4 py-2.5 ${inputBg} border border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-400 transition-colors text-sm`}>
                      <option value="">Select experience level</option>
                      <option value="Less than 1 year">Less than 1 year</option>
                      <option value="1–2 years">1–2 years</option>
                      <option value="3–5 years">3–5 years</option>
                      <option value="5–10 years">5–10 years</option>
                      <option value="10+ years">10+ years</option>
                    </select>
                  </motion.div>
                </motion.div>

                {/* Position */}
                <motion.div variants={fadeUp}>
                  <label htmlFor="position" className="block text-white text-sm font-medium mb-1">Position of Interest <span className="text-gray-500 font-normal">(optional)</span></label>
                  <select id="position" name="position" value={formData.position} onChange={handleInputChange} disabled={isSubmitting}
                    className={`w-full px-4 py-2.5 ${inputBg} border border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-400 transition-colors text-sm`}>
                    <option value="">General Application / Not Listed</option>
                    {openRoles.map((role) => (
                      <option key={role.title} value={role.title}>{role.title}</option>
                    ))}
                  </select>
                </motion.div>

                {/* Cover Letter */}
                <motion.div variants={fadeUp}>
                  <label htmlFor="message" className="block text-white text-sm font-medium mb-1">Cover Letter / Tell Us About Yourself</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleInputChange}
                    placeholder="Tell us about your experience, skills, and why you'd like to join Fiieve..."
                    rows={4} disabled={isSubmitting}
                    className={`w-full px-4 py-2.5 ${inputBg} border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-400 transition-colors text-sm`}
                  />
                </motion.div>

                {/* ✅ CV note — EmailJS doesn't support file uploads */}
                <motion.div variants={fadeUp}>
                  <div className={`w-full px-4 py-3 ${inputBg} border border-green-400/30 rounded-lg`}>
                    <p className="text-gray-300 text-sm">
                      📎 <strong className="text-white">Attach your CV</strong> — please email it to{" "}
                      <a href="mailto:info@fiieve.com" className="text-green-400 hover:underline">info@fiieve.com</a>{" "}
                      with subject: <span className="text-green-400 font-medium">CV — [Your Name] — [Position]</span>
                    </p>
                  </div>
                </motion.div>

                {/* Submit */}
                <motion.button variants={fadeUp} whileHover={{ scale: 1.02 }} type="submit" disabled={isSubmitting}
                  className="w-full bg-green-400 hover:bg-green-300 text-black font-bold py-3 rounded-lg transition-all duration-300 text-base disabled:opacity-50">
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Sending...
                    </span>
                  ) : submitted ? "✓ Application Sent!" : "Submit Application"}
                </motion.button>

                {submitted && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} role="status" aria-live="polite"
                    className="bg-green-500/10 border border-green-500/50 text-green-400 px-6 py-4 rounded-xl text-center text-sm">
                    ✓ Application received! We'll be in touch within 48 hours. Don't forget to email your CV to{" "}
                    <a href="mailto:info@fiieve.com" className="underline">info@fiieve.com</a>.
                  </motion.div>
                )}

                {submitError && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} role="alert" aria-live="assertive"
                    className="bg-red-500/10 border border-red-500/50 text-red-400 px-6 py-4 rounded-xl text-center text-sm">
                    {submitError}
                  </motion.div>
                )}

              </motion.form>
            </motion.div>
          </section>

        </div>
      </motion.main>
    </>
  );
}